/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { type FC } from 'react';

import copy from 'copy-to-clipboard';
import { I18n } from '@coze-arch/i18n';
import {
  IconCozCheckMarkCircleFillPalette,
  IconCozEdit,
  IconCozLink,
  IconCozUserPermission,
} from '@coze-arch/coze-design/icons';
import { Avatar, Toast } from '@coze-arch/coze-design';
import { formatDate } from '@coze-arch/bot-utils';
import { buildCozeChatUrl } from '@coze-agent-ide/abr-chat-frame';

import { type PublishedAppItem } from '../../api/published-apps';

import styles from './index.module.less';

export interface AppStoreCardProps {
  item: PublishedAppItem;
  onOpen: (item: PublishedAppItem) => void;
  onCopy: (item: PublishedAppItem) => void;
  onEdit: (item: PublishedAppItem) => void;
  onViewScope: (item: PublishedAppItem) => void;
}

export const AppStoreCard: FC<AppStoreCardProps> = ({
  item,
  onOpen,
  onCopy,
  onEdit,
  onViewScope,
}) => {
  const publishedTime = item.published_at
    ? formatDate(Math.floor(item.published_at / 1000), 'YYYY-MM-DD HH:mm')
    : '—';

  const inferenceLabel =
    item.inference_label?.trim() || I18n.t('abr_app_info_unconfigured');

  return (
    <article className={styles.card}>
      <div className={styles.cardTags}>
        <span className={styles.tagAgent}>AGENT</span>
        <span className={styles.tagLive}>LIVE</span>
      </div>

      <div className={styles.cardPanel}>
        <div className={styles.cardMainRow}>
          <div className={styles.cardMainLeft}>
            <div className={styles.titleRow}>
              <h3 className={styles.cardTitle}>{item.title}</h3>
              <IconCozCheckMarkCircleFillPalette className={styles.liveIcon} />
            </div>
            <p className={styles.cardDesc}>{item.description || '—'}</p>
          </div>
          <Avatar
            className={styles.cardAvatar}
            shape="square"
            src={item.icon_url || undefined}
          >
            {(item.title || 'A').slice(0, 1).toUpperCase()}
          </Avatar>
        </div>

        <span className={styles.typeTag}>
          {I18n.t('abr_app_store_type_agent')}
        </span>

        <div className={styles.llmRow}>
          <span className={styles.llmLabel}>
            {I18n.t('abr_app_info_inference')}
          </span>
          <span className={styles.llmChip}>{inferenceLabel}</span>
        </div>

        <div className={styles.publishMeta}>
          {item.publisher_name} · {I18n.t('abr_app_store_published_at')}{' '}
          {publishedTime}
        </div>
      </div>

      <div className={styles.cardActions}>
        <button
          type="button"
          className={styles.btnOpen}
          onClick={() => onOpen(item)}
        >
          {I18n.t('abr_app_open')}
        </button>
        {item.is_publisher ? (
          <>
            <button
              type="button"
              className={styles.btnIcon}
              title={I18n.t('abr_app_store_view_scope')}
              onClick={() => onViewScope(item)}
            >
              <IconCozUserPermission />
            </button>
            <button
              type="button"
              className={styles.btnIcon}
              title={I18n.t('abr_app_continue_edit')}
              onClick={() => onEdit(item)}
            >
              <IconCozEdit />
            </button>
          </>
        ) : null}
        <button
          type="button"
          className={styles.btnIcon}
          title={I18n.t('abr_app_copy_link')}
          onClick={() => onCopy(item)}
        >
          <IconCozLink />
        </button>
      </div>
    </article>
  );
};

export const copyPublishedAppLink = (item: PublishedAppItem): boolean => {
  const url = buildCozeChatUrl({
    botId: item.bot_id,
    spaceId: item.space_id,
    scene: 'release',
    entry: 'standalone',
  });
  if (!url) {
    Toast.error(I18n.t('abr_app_webchat_url_missing'));
    return false;
  }
  if (copy(url)) {
    Toast.success(I18n.t('abr_app_copy_success'));
    return true;
  }
  return false;
};

export const openPublishedApp = (item: PublishedAppItem): void => {
  const url = buildCozeChatUrl({
    botId: item.bot_id,
    spaceId: item.space_id,
    scene: 'release',
    entry: 'standalone',
  });
  if (!url) {
    Toast.error(I18n.t('abr_app_webchat_url_missing'));
    return;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
};
